import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { useAppStore } from '../../stores/appStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '../../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertCircle, ArrowLeft, Loader2, Plus, X } from 'lucide-react';
import { toast } from '../../components/ui/toast';
import { createRole } from '../../services/api';

const createRoleSchema = z.object({
  display_name: z.string().max(100, 'Display name must be 100 characters or less').optional(),
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be 50 characters or less')
    .regex(/^[a-zA-Z0-9_]+$/, 'Name must be alphanumeric with underscores only'),
  description: z.string().max(255, 'Description must be 255 characters or less').optional(),
  serviceid: z.string().max(100, 'Service ID must be 100 characters or less').optional(),
  permissions: z.array(z.object({ value: z.string().min(1, 'Permission cannot be empty') })).optional(),
  organization_id: z.string().optional(),
});

export function CreateRole() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const { organizations, fetchOrganizations } = useAppStore();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPermission, setNewPermission] = useState('');

  const stateOrgId = (location.state as any)?.orgId;

  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchOrganizations().catch(() => {});
    }
  }, [isSuperAdmin, fetchOrganizations]);

  const form = useForm<z.infer<typeof createRoleSchema>>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      display_name: '',
      name: '',
      description: '',
      serviceid: '',
      permissions: [],
      organization_id: stateOrgId || 'system',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'permissions',
  });

  const handleAddPermission = () => {
    const trimmed = newPermission.trim();
    if (!trimmed) return;
    append({ value: trimmed });
    setNewPermission('');
  };

  const onSubmit = async (data: z.infer<typeof createRoleSchema>) => {
    setIsSubmitting(true);
    setError(null);
    const targetOrgId = data.organization_id && data.organization_id !== 'system' ? data.organization_id : 'system';
    try {
      await createRole(targetOrgId, {
        display_name: data.display_name || '',
        name: data.name,
        description: data.description || '',
        serviceid: data.serviceid || '',
        permissions: (data.permissions || []).map(p => p.value),
      });

      toast({
        title: 'Role Created',
        description: `Role "${data.name}" has been created successfully.`,
      });

      navigate('/dashboard/roles', { state: { orgId: targetOrgId } });
    } catch (err: any) {
      setError(err.message || 'Failed to create role');
      toast({
        title: 'Creation Failed',
        description: err.message || 'Failed to create role',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>Only super administrators can create roles.</AlertDescription>
        </Alert>
        <Link to="/dashboard/roles">
          <Button variant="outline">Back to Roles</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <Link to="/dashboard/roles">
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Roles
          </Button>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Role</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Define a new custom role</p>
            </div>
          </div>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Role Details</CardTitle>
          <CardDescription>Fill in the details for the new role</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="display_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Content Editor" {...field} />
                    </FormControl>
                    <FormDescription>
                      A human-readable name (e.g. "Content Editor")
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="editor" {...field} />
                    </FormControl>
                    <FormDescription>
                      Letters, numbers, and underscores only (e.g. "Content_Editor")
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="Describe what this role can do..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="serviceid"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service ID (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="service_123" {...field} />
                    </FormControl>
                    <FormDescription>
                      Optional ID to link this role to an external service.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="organization_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scope</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="System-wide (no organization)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="system">System-wide</SelectItem>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select "System Wide" for a global role, or pick an organization to scope it.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Permissions */}
              <div className="space-y-2">
                <FormLabel>Permissions</FormLabel>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. read:content"
                    value={newPermission}
                    onChange={(e) => setNewPermission(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPermission();
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={handleAddPermission}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {fields.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {fields.map((field, index) => (
                      <span
                        key={field.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-sm font-mono"
                      >
                        {form.getValues(`permissions.${index}.value`)}
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="hover:text-red-600 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Press Enter or click + to add a permission
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {isSubmitting ? 'Creating...' : 'Create Role'}
                </Button>
                <Link to="/dashboard/roles">
                  <Button variant="outline" type="button">Cancel</Button>
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
